import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { loanApi, loanConfigApi, type ApplyLoanPayload, type LoanStatus, type SaveLoanConfigInput } from "../lib/loanApi";
import { apiRequest } from "../lib/api";

export function useMyProfile() {
  return useQuery({
    queryKey: ["my-profile"] as const,
    queryFn: () => apiRequest<{ baseSalary: number | null; position?: string; department?: string; hireDate?: string }>("/me/profile"),
    staleTime: 1000 * 60 * 5,
  });
}

export const LOAN_KEYS = {
  all: ["loans"] as const,
  mine: (status?: LoanStatus) => [...LOAN_KEYS.all, "mine", status ?? "ALL"] as const,
  detail: (id: string) => [...LOAN_KEYS.all, "detail", id] as const,
  adminList: (businessId: string, status: string) =>
    [...LOAN_KEYS.all, "admin", businessId, status] as const,
};

const LOAN_QUERY_OPTIONS = {
  staleTime: 0,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
} as const;

export function useMyLoans(status?: LoanStatus) {
  return useQuery({
    queryKey: LOAN_KEYS.mine(status),
    queryFn: () => loanApi.listMine(status).then((r) => r.items),
    ...LOAN_QUERY_OPTIONS,
  });
}

export function useMyLoan(loanId: string) {
  return useQuery({
    queryKey: LOAN_KEYS.detail(loanId),
    queryFn: () => loanApi.getMine(loanId),
    ...LOAN_QUERY_OPTIONS,
  });
}

export function useAdminLoans(businessId: string, status = "PENDING", enabled = true) {
  return useQuery({
    queryKey: LOAN_KEYS.adminList(businessId, status),
    queryFn: () => loanApi.adminList(businessId, status),
    enabled: enabled && !!businessId,
    ...LOAN_QUERY_OPTIONS,
  });
}

export function useApplyLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ApplyLoanPayload) => loanApi.apply(payload),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: LOAN_KEYS.all });
    },
  });
}

export function useApproveLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      loanId,
      userId,
      businessId,
    }: {
      loanId: string;
      userId: string;
      businessId: string;
    }) => loanApi.approve(loanId, userId, businessId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: LOAN_KEYS.all });
    },
  });
}

export function useRejectLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      loanId,
      userId,
      businessId,
      reason,
    }: {
      loanId: string;
      userId: string;
      businessId: string;
      reason: string;
    }) => loanApi.reject(loanId, userId, businessId, reason),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: LOAN_KEYS.all });
    },
  });
}

export function useReleaseLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      loanId,
      userId,
      businessId,
      releaseDate,
    }: {
      loanId: string;
      userId: string;
      businessId: string;
      releaseDate: string;
    }) => loanApi.release(loanId, userId, businessId, releaseDate),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: LOAN_KEYS.all });
    },
  });
}

export function useLoanConfig(businessId: string) {
  return useQuery({
    queryKey: ["loanConfig", businessId] as const,
    queryFn: () => loanConfigApi.get(businessId).then((r) => r.config),
    enabled: !!businessId,
  });
}

export function useSaveLoanConfig(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveLoanConfigInput) => loanConfigApi.save(businessId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["loanConfig", businessId] }),
  });
}
